import { css, keyframes } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';

import IconSpinner3 from '~icons/ei/spinner-3';

import { theme } from '@adflow/frontend/ui-theme';

const spinning = keyframes({
	from: {
		transform: 'rotate(0deg)',
	},
	to: {
		transform: 'rotate(360deg)',
	},
});

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const styles = {
	container: css({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-around',
		width: '100%',
		height: '100%',
		overflow: 'hidden',
	}),
	containerInline: css({
		display: 'inline-block',
		verticalAlign: 'middle',
	}),
	spinner: css({
		width: '100%',
		height: '100%',
		maxWidth: '4rem',
		maxHeight: '4rem',
		color: theme.colors.text.hint,
		animation: `${spinning} 1.2s linear infinite, ${fadeIn} 0.3s both`,
	}),
	spinnerImmediate: css({ animation: `${spinning} 1.2s linear infinite` }),
};

interface LoadingIndicatorProps {
	immediate?: boolean; // display spinner immediately instead of after a delay
	color?: string;
	className?: string;
	containerStyle?: CSSInterpolation;
}

/** Display a loading spinner in the center of the container. */
export function LoadingIndicator({
	immediate = false,
	color,
	className,
	containerStyle,
}: LoadingIndicatorProps) {
	return (
		<div
			css={[styles.container, containerStyle]}
			className={className}
			data-testid="loading-spinner"
		>
			<IconSpinner3
				css={[
					styles.spinner,
					immediate ? styles.spinnerImmediate : undefined,
					color ? { color } : undefined,
				]}
			/>
		</div>
	);
}

/** Display a loading spinner inline with some text. */
export function LoadingIndicatorInline({
	immediate = false,
	color,
	className,
}: LoadingIndicatorProps) {
	return (
		<span
			css={styles.containerInline}
			className={className}
			data-testid="loading-spinner"
		>
			<IconSpinner3
				css={[
					styles.spinner,
					immediate ? styles.spinnerImmediate : undefined,
					color ? { color } : undefined,
				]}
			/>
		</span>
	);
}
